import React, { useContext, useEffect, useState } from "react"
import { GetApi } from "../../../../../../api/api"
import { Spinner, TreeView, TreeViewItem } from "@trimbleinc/modus-react-bootstrap"
import "./Attributes.scss"
import { useParams } from "react-router-dom"
import { FeatureCatalogContext } from "../../../../FeatureCatalogProvider"
import attributeTypeIcon from "../../../../Assets/Icons/attributeType.svg"
import inheritedAttributeTypeIcon from "../../../../Assets/Icons/inheritedAttributeType.svg"
import "./Attributes.scss"
import AttributeProperties from "../AttributeProperties/AttributeProperties"
import useQuery from "../../../UseQuery"

interface IAttributeType {
  name?: string
  pid: number
  dataType?: string
  description?: string
  inherited: boolean
}

const AttributeTypeTreeItem = (props: { attributeType: IAttributeType; expandedNodes: number[] }) => {
  const context = useContext(FeatureCatalogContext)
  const { catalog } = useParams<{ catalog: string; type: string; pid: string }>()
  const [attributeTypeChildren, setAttributeTypeChildren] = useState(new Array<IAttributeType>())
  const [attributeTypeMeasure, setAttributeTypeMeasure] = useState(String)
  const language = useQuery().get("language")

  useEffect(() => {
    if (props.attributeType.dataType !== "Struct" && props.attributeType.dataType !== "List") {
      // If datatype is measure, get MeasureType
      if (props.attributeType.dataType === "Measure") {
        const getAttributeProperties = async () => {
          GetApi().then((api) => {
            api.getAnyAttributeType(catalog, props.attributeType.pid, language).then((response) => {
              // set datatype as MeasureType
              setAttributeTypeMeasure(response.measureType!)
            })
          })
        }

        getAttributeProperties()
        return
      }
      return
    }
    const getAttributeTypeChild = async () => {
      GetApi().then((api) => {
        api.getAnyAttributeType(catalog, props.attributeType.pid, language).then((response) => {
          let result = new Array<IAttributeType>()
          if (response.dataType === "Struct") {
            if (response.structContent === undefined) {
              return
            }
            result = response.structContent!.map((attributeType) => {
              return {
                name: attributeType.name,
                pid: attributeType.id!,
                dataType: attributeType.dataType,
                inherited: false
              }
            })
          }
          if (response.dataType === "List") {
            if (response.listContent === undefined) {
              return
            }
            // If parent is a list type ---> concatinate with (0..*) notation
            props.attributeType.name = response.listContent!.name
            props.attributeType.pid = response.listContent!.id!
            props.attributeType.dataType = AddZeroToManyNotation(response.listContent!.dataType as string)
          }
          setAttributeTypeChildren(result)
        })
      })
    }

    getAttributeTypeChild()
  }, [catalog, language])

  const label = (
    <div className="pr-3">
      <img src={props.attributeType.inherited ? inheritedAttributeTypeIcon : attributeTypeIcon} />
      <span className="attributeName">{props.attributeType.name} </span>
      <span className="badge badge-sm badge-text-secondary">{props.attributeType.pid}</span>
      <span className="badge badge-tertiary">
        {props.attributeType.dataType === "Measure" ? attributeTypeMeasure : props.attributeType.dataType}
      </span>
    </div>
  )

  const onClick = () => {
    context.setAtId(props.attributeType.pid)
  }

  if (attributeTypeChildren.length > 0) {
    return (
      <div>
        <TreeViewItem
          nodeId={props.attributeType.pid}
          id="attributeType"
          label={label}
          className="card-text-2"
          onClick={onClick}
        >
          {context.atId !== null &&
            props.expandedNodes.includes(props.attributeType.pid) &&
            context.atId === props.attributeType.pid && <AttributeProperties />}
          {attributeTypeChildren.map((attributeChild: IAttributeType) => {
            return (
              <AttributeTypeTreeItem
                attributeType={attributeChild}
                key={attributeChild.pid}
                expandedNodes={props.expandedNodes}
              />
            )
          })}
        </TreeViewItem>
        {context.atId !== null &&
          !props.expandedNodes.includes(props.attributeType.pid) &&
          context.atId === props.attributeType.pid && <AttributeProperties />}
      </div>
    )
  } else {
    return (
      <div>
        <TreeViewItem
          nodeId={props.attributeType.pid}
          id="attributeType"
          label={label}
          className="card-text-2"
          onClick={onClick}
        >
          {attributeTypeChildren.map((attributeChild: IAttributeType) => {
            return (
              <AttributeTypeTreeItem
                attributeType={attributeChild}
                key={attributeChild.pid}
                expandedNodes={props.expandedNodes}
              />
            )
          })}
        </TreeViewItem>
        {context.atId !== null &&
          !props.expandedNodes.includes(props.attributeType.pid) &&
          context.atId === props.attributeType.pid && <AttributeProperties />}
      </div>
    )
  }
}

const AttributeTypes = () => {
  const { catalog, pid } = useParams<{ catalog: string; type: string; pid: string }>()
  const [attributeTypes, setAttributeTypes] = useState(new Array<IAttributeType>())
  const [isLoading, setIsLoading] = useState(true)
  const [expandedNodes, setExpandedNodes] = useState(new Array<number>())
  const language = useQuery().get("language")

  useEffect(() => {
    const getAttributeTypes = async () => {
      GetApi().then((api) => {
        api.getAnyFeatureType(catalog, parseInt(pid, 10), true, language).then((response) => {
          const attributes = new Array<IAttributeType>()
          response.attributeTypes?.map((attributeType) => {
            attributes.push({
              name: attributeType.name,
              pid: attributeType.attributeTypeId,
              dataType: attributeType.dataType,
              inherited: false
            })
          })
          response.inheritedAttributeTypes?.map((attributeType) => {
            attributes.push({
              name: attributeType.name,
              pid: attributeType.attributeTypeId,
              dataType: attributeType.dataType,
              inherited: true
            })
          })
          attributes.sort((a, b) =>
            a.name!.localeCompare(b.name!, undefined, { sensitivity: "base", ignorePunctuation: true })
          )
          setAttributeTypes(attributes)
          setIsLoading(false)
        })
      })
    }

    getAttributeTypes()
  }, [catalog, pid, language])

  const handleNodeSelect = (event: React.SyntheticEvent, nodeIds: number[]) => {
    setExpandedNodes(nodeIds)
  }

  const onResize = () => {}
  const onResizeCapture = () => {}

  return (
    <div>
      <TreeView
        className="list-group-borderless"
        nodeId={0}
        id="basic"
        onNodeToggle={handleNodeSelect}
        onResize={onResize}
        onResizeCapture={onResizeCapture}
      >
        <TreeViewItem nodeId={1} label={"ATTRIBUTES"} id="headerInPanel" className="card-subtitle-2" />
        {isLoading ? (
          <TreeViewItem
            nodeId={2}
            label={<Spinner animation="border" role="status" variant="primary" />}
            id="loaderitem"
            className="card-text-2"
          />
        ) : (
          <></>
        )}
        {attributeTypes.map((attributeType) => (
          <AttributeTypeTreeItem attributeType={attributeType} key={attributeType.pid} expandedNodes={expandedNodes} />
        ))}
      </TreeView>
    </div>
  )
}

export default AttributeTypes

function AddZeroToManyNotation(str: string) {
  return str + " " + "(0..*)"
}
